// import { format } from "date-fns";

import { IFilterState } from 'types/Form';

import dayjs from 'dayjs';
import { IClaim, IClaimComponents } from 'types/Claim';
import { FieldAmount, IAmountLimit } from 'types/Member';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { getLanguageStorage } from './localStorage';

export const validateEmail = (email: string) => {
	return String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		);
};

export const capitalize = (str: string) => {
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const camelcase = (str: string) => {
	return str.charAt(0).toUpperCase() + str.slice(1);
};

export const camelcaseToUnderscore = (str: string) => {
	if (!str) return '';
	return str
		.split(/(?=[A-Z])/)
		.join('_')
		.toLowerCase();
};

export const pxToRem = (px: number) => {
	return px / 16;
};

export const getStatusString = (string: string) => {
	switch (string) {
		case 'NEW':
		case 'WAITING_FEEDBACK':
		case 'APPROVED':
			return 'Pending';
		case 'MEMBER_PAID':
			return 'Paid';
		case 'COMPLETED':
			return 'Completed';
		case 'CANCELED':
			return 'Canceled';
	}
};

export const getShortDate = (date?: string) => {
	if (!date) return '';
	let d = new Date(date);
	d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);

	return d.toLocaleDateString(getLanguageStorage(), {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	});
};

export const getShortTime = (date?: string) => {
	if (!date) return '';
	var d = new Date(date);
	d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);

	return d
		.toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		})
		.toUpperCase();
};

export const getFullDate = (date?: string) => {
	if (!date) return '';

	return `${getShortDate(date)} ${getShortTime(date)}`;
};

export const formatDayjsForBE = (date: dayjs.Dayjs | null) => {
	return dayjs(date).format('YYYY-MM-DD');
};

export const formatDayjsMonthForBE = (date: dayjs.Dayjs | null) => {
	return dayjs(date).format('YYYY-MM');
};

export const dayjsForMaterial = (date?: dayjs.Dayjs | null) => {
	if (!date) return undefined;
	return date
		.set('hour', 0)
		.set('minute', 0)
		.set('second', 0)
		.set('millisecond', 0);
};

export const dateZuluToDayjs = (date: string) => {
	var d = new Date(date.split('T')[0]);
	d = new Date(d.getTime() + d.getTimezoneOffset() * 60000);

	return dayjs(d);
};

export const formatPrice = (
	number?: number | string,
	showCurrency: boolean = true,
) => {
	if (number === undefined) return '';
	const n = Number(number);
	return new Intl.NumberFormat(getLanguageStorage(), {
		style: 'currency',
		currency: 'EUR',
	})
		.format(n)
		.replace('-', '- ')
		.replace('€', '')
		.trim()
		.concat(showCurrency ? ' €' : ''); // currency always at the end
};

export const serviceIdToLabel = (serviceId: number) => {
	const prefix = 'newClaim.priceEstimate.';
	const prefixOther = `newClaim.priceEstimate.otherReceiptItems.`;

	switch (serviceId) {
		case 1:
			return `${prefix}lenses`;
		case 2:
			return `${prefix}frame`;
		case 3:
			return `${prefix}exam`;
		case 4:
			return `${prefix}frameAndLenses`;
		case 5:
			return `${prefix}contacts`;
		case 6:
			return `${prefixOther}service.fitting`;
		case 7:
			return `${prefixOther}service.warranty`;
		case 8:
			return `${prefixOther}service.second-pair`;
		case 9:
			return `${prefixOther}service.cleaning-kit`;
		case 10:
			return `${prefixOther}service.generic`;
		case 11:
			return `${prefixOther}service.vat`;
		case 12:
			return `${prefix}lensesMonofocal`;
		case 13:
			return `${prefix}lensesBifocal`;
		case 14:
			return `${prefix}lensesTrifocal`;
		case 15:
			return `${prefix}lensesProgressive`;
		case 16:
			return `${prefix}frameAndLensesMonofocal`;
		case 17:
			return `${prefix}frameAndLensesBifocal`;
		case 18:
			return `${prefix}frameAndLensesTrifocal`;
		case 19:
			return `${prefix}frameAndLensesProgressive`;
		case 20:
			return `${prefix}examVdu`;
		default:
			return '';
	}
};

export const getRangeValueAmount = (
	serviceId: number | null,
	benefit: { amountLimits: IAmountLimit[]; serviceId: number }[],
	field: FieldAmount,
) => {
	const values = benefit
		.find(b => b.serviceId === serviceId)
		?.amountLimits.find(amount => amount.field === field);
	return { max: values?.maxLength };
};

export const useGetIsOtherComponent = () => {
	const { otherServices } = useSelector((state: RootState) => state.claim);

	const isOtherComponent = (component: IClaimComponents) => {
		return (
			otherServices.map(item => item.serviceId).includes(component.serviceId!) ||
			!component.serviceId
		);
	};

	return { isOtherComponent };
};

export const roundingPrice100 = (price: string | number | undefined) => {
	const value = parseFloat((price ?? '').toString());
	return Number.isNaN(value) ? 0 : Math.round(value * 100);
};

export const formatNumber = (value: string, isCurrency: boolean) => {
	const pattern = /^(:?(?:[+-]?[0-9]+([.,])?([0-9]+)??)+[+-]?)$/;

	if (value.match(pattern)) {
		let returnValue = value.replaceAll(/,/g, '.');
		const dotPosition = returnValue.search(/\./);

		if (dotPosition !== -1) {
			//se sono stati inseriti più punti andiamo ad eliminare la seconda occorrenza
			if (returnValue.split('').filter(v => v === '.')?.length > 1) {
				const newString = returnValue
					.substring(dotPosition + 1, returnValue.length)
					.replace('.', '');
				returnValue = returnValue.substring(0, dotPosition + 1) + newString;
			}
			//se sono stati inseriti 3 caratteri dopo il punto andiamo ad eliminare l'ultimo carattere
			if (dotPosition + 3 < value.length) {
				returnValue = returnValue.substring(0, dotPosition + 3);
			}
		}

		if (isCurrency) {
			returnValue = returnValue.replace('+', '').replace('-', '');
		}

		return returnValue;
	}

	return '';
};

export const formatInputNumberValue = (value: string | number) => {
	const lng = getLanguageStorage();

	if (lng.toLowerCase() === 'en-gb' || lng.toLowerCase() === 'en-us') {
		return value.toString().replace(',', '.');
	} else {
		return value.toString().replace('.', ',');
	}
};

export const copyToClipboard = (text: string) => {
	navigator.clipboard.writeText(text);
};

export const getLocaleLanguage = () => {
	return getLanguageStorage().substring(0, 2);
};

export const createValueSearchMember = (memberValues: IFilterState) => {
	const {
		inputFiscalCode,
		inputMemberId,
		inputFirstName,
		inputLastName,
		inputDateOfBirth,
		dateOfService,
	} = memberValues;

	let values;
	if (inputFiscalCode !== '') {
		values = {
			size: 10,
			fiscal_code: inputFiscalCode,
			date_of_service: dateOfService,
		};
	}
	if (inputMemberId !== '') {
		values = {
			size: 10,
			member_id: inputMemberId,
			date_of_service: dateOfService,
		};
	}
	if (
		inputFirstName !== '' &&
		inputLastName !== '' &&
		inputDateOfBirth !== null
	) {
		values = {
			size: 10,
			first_name: inputFirstName,
			last_name: inputLastName,
			birth_date: inputDateOfBirth,
			date_of_service: dateOfService,
		};
	}

	return values;
};

/**
 * Builds filter parameters for claims search (GET) and download (POST).
 *
 * Returns:
 * - queryParams: URL query string for GET /claim (e.g. "&firstName=John&claimStatus=OPEN")
 * - body: JSON body for POST /claim/downloadClaimList
 *
 * Note: clientCodes is a string in queryParams but an array of strings in body.
 * Note: Spaces in string values are URL-encoded (%20) only in queryParams.
 */
export const buildClaimsFilterParams = (claimsSearchValues: IFilterState) => {
	const {
		inputExternalClaimId,
		inputCarrierClaimId,
		inputFirstName,
		inputLastName,
		dateOfService,
		inputClaimStatus,
		inputClaimNetworkStatus,
		inputClaimReturnStatus,
		inputClient,
		inputHasComplaint,
		inputHasFraud,
		inputMemberLocation,
		inputClaimMarkets,
	} = claimsSearchValues;

	// "All" means no filter, treat as null
	const clientValue = inputClient && inputClient !== 'All' ? inputClient : null;
	// "All" means no filter; otherwise convert to boolean (true = In-Network)
	const inNetworkValue =
		inputClaimNetworkStatus && inputClaimNetworkStatus !== 'All'
			? inputClaimNetworkStatus === 'INN'
			: null;

	// Each filter defined once: [queryParamKey, value]
	const filters: [string, string | boolean | null][] = [
		['externalClaimId', inputExternalClaimId || null],
		['carrierClaimId', inputCarrierClaimId || null],
		['clientCodes', clientValue],
		['firstName', inputFirstName || null],
		['lastName', inputLastName || null],
		['serviceDate', dateOfService ? formatDayjsForBE(dateOfService) : null],
		['claimStatus', inputClaimStatus || null],
		['inNetwork', inNetworkValue],
		['hasComplaint', inputHasComplaint || null],
		['hasFraud', inputHasFraud || null],
		['returnStatus', inputClaimReturnStatus || null],
		['memberLocation', inputMemberLocation || null],
	];

	const arrayFilters: [string, string[]][] = [
		['networkCodes', inputClaimMarkets || ['All']],
	];

	let queryParams = '';
	const body: Record<string, string | boolean | string[] | null> = {};

	for (const [key, value] of filters) {
		if (value === null) continue;

		// Query string: URL-encode spaces in string values
		const qValue =
			typeof value === 'string' ? value.replaceAll(' ', '%20') : value;
		queryParams += `&${key}=${qValue}`;

		// Body: clientCodes must be an array of strings
		body[key] =
			key === 'clientCodes' && typeof value === 'string' ? [value] : value;
	}

	for (const [key, values] of arrayFilters) {
		if (!values || values.includes('All')) continue;

		queryParams += values.map(v => `&${key}=${v}`).join('');
		body[key] = values;
	}

	return { queryParams, body };
};

export const createPaymentsSearchValues = (
	paymentsSearchValues: IFilterState,
) => {
	const { inputReferenceNumber, inputTotalAmount, dateOfService } =
		paymentsSearchValues;

	let searchValues = '';
	if (inputReferenceNumber) {
		searchValues = searchValues.concat(
			`&referenceNumber=${inputReferenceNumber}`,
		);
	}
	if (inputTotalAmount) {
		searchValues = searchValues.concat(`&totalAmount=${inputTotalAmount}`);
	}
	if (dateOfService) {
		searchValues = searchValues.concat(
			`&processDate=${formatDayjsForBE(dateOfService)}`,
		);
	}
	return searchValues;
};

/**
 * Builds filter parameters for billing search (GET) and actions (POST/PUT).
 *
 * Returns:
 * - queryParams: URL query string for GET /billings/...
 * - body: JSON body for POST/PUT billing endpoints
 *
 * Note: Array filters (typologies, carriers, clients, groups) become repeated
 *       query params (e.g. "&carrierCodes=A&carrierCodes=B") but arrays in body.
 * Note: Date ranges default end date to start date if only start is provided.
 * Note: body excludes null/empty values (cleaned up before returning).
 */
export const buildBillingFilterParams = (
	billingsSearchValues: IFilterState,
) => {
	const {
		inputBillingId,
		inputBillingTypologies,
		inputBillingStatus,
		inputBillingCarriers,
		inputClients,
		inputGroups,
		inputExternalClaimId,
		inputCarrierClaimId,
		inputClaimStartDate,
		inputClaimEndDate,
		inputPaidStartDate,
		inputPaidEndDate,
		inputClaimStatus,
		inputBillingNetworkStatus,
		inputClaimBillingStartDate,
		inputClaimBillingEndDate,
		inputClaimSettlementStartDate,
		inputClaimSettlementEndDate,
		inputBillingMarkets,
	} = billingsSearchValues;

	// "All" means no filter; otherwise convert to boolean (true = In-Network)
	const inNetworkValue =
		inputBillingNetworkStatus && inputBillingNetworkStatus !== 'All'
			? inputBillingNetworkStatus === 'INN'
			: null;

	// --- Simple string filters: [queryParamKey, value] ---
	const simpleFilters: [string, string | boolean | null][] = [
		['billingId', inputBillingId || null],
		['billingStatusCode', inputBillingStatus || null],
		['externalClaimId', inputExternalClaimId || null],
		['carrierClaimId', inputCarrierClaimId || null],
		['claimStatus', inputClaimStatus || null],
		['inNetwork', inNetworkValue],
	];

	// --- Array filters: [queryParamKey, bodyKey, values] ---
	// "All" in the array means no filter
	const arrayFilters: [string, string, string[]][] = [
		['billingTypologies', 'billingType', inputBillingTypologies],
		['carrierCodes', 'carrierCodes', inputBillingCarriers],
		['clientCodes', 'clientCodes', inputClients],
		['groupCodes', 'groupCodes', inputGroups],
		['networkCodes', 'networkCodes', inputBillingMarkets],
	];

	// --- Date range filters: [startKey, endKey, startDate, endDate] ---
	// If only start is provided, end defaults to start
	const dateRangeFilters: [
		string,
		string,
		typeof inputClaimStartDate,
		typeof inputClaimEndDate,
	][] = [
		['claimDateStart', 'claimDateEnd', inputClaimStartDate, inputClaimEndDate],
		['paidDateStart', 'paidDateEnd', inputPaidStartDate, inputPaidEndDate],
		[
			'billingDateStart',
			'billingDateEnd',
			inputClaimBillingStartDate,
			inputClaimBillingEndDate,
		],
		[
			'settlementDateStart',
			'settlementDateEnd',
			inputClaimSettlementStartDate,
			inputClaimSettlementEndDate,
		],
	];

	// --- Build query string and body ---

	let queryParams = '';
	const body: Record<string, any> = {};

	for (const [key, value] of simpleFilters) {
		if (value === null) continue;
		queryParams += `&${key}=${value}`;
		body[key] = value;
	}

	for (const [queryKey, bodyKey, values] of arrayFilters) {
		if (!values || values.includes('All')) continue;
		queryParams += values.map(v => `&${queryKey}=${v}`).join('');
		body[bodyKey] = values;
	}

	for (const [startKey, endKey, startDate, endDate] of dateRangeFilters) {
		if (!startDate) continue;
		const start = formatDayjsForBE(startDate);
		const end = endDate ? formatDayjsForBE(endDate) : start;
		queryParams += `&${startKey}=${start}&${endKey}=${end}`;
		body[startKey] = start;
		body[endKey] = end;
	}

	return { queryParams, body };
};

export const buildMembersBillingFilterParams = (
	billingsSearchValues: IFilterState,
	isAggregation = false,
) => {
	const {
		inputMemberBillingId,
		inputMemberBillingStatus,
		inputMemberBillingMonthStart,
		inputMemberBillingMonthEnd,
		inputMemberClient,
		inputMemberGroups,
		inputMemberPlanCodes,
		inputAggregationBillingMonthStart,
		inputAggregationBillingMonthEnd,
		inputAggregationClient,
		inputAggregationGroups,
		inputAggregationPlanCodes,
		inputMemberDivisionCode,
		inputMemberLastName,
		inputMemberFirstName,
		inputMemberRelationshipTypes,
		inputMemberMemberId,
		inputMemberEmMemberId,
		inputMemberSubscriptionStartDate,
		inputMemberSubscriptionEndDate,
	} = billingsSearchValues;

	// Pick the correct set of shared filters based on context
	const billingMonthStart = isAggregation
		? inputAggregationBillingMonthStart
		: inputMemberBillingMonthStart;
	const billingMonthEnd = isAggregation
		? inputAggregationBillingMonthEnd
		: inputMemberBillingMonthEnd;
	const clientCodes = isAggregation ? inputAggregationClient : inputMemberClient;
	const groupCodes = isAggregation ? inputAggregationGroups : inputMemberGroups;
	const planCodes = isAggregation ? inputAggregationPlanCodes : inputMemberPlanCodes;

	const simpleFilters: [string, string | null][] = [
		['billingId', inputMemberBillingId || null],
		['billingStatusCode', inputMemberBillingStatus || null],
		['divisionCode', inputMemberDivisionCode || null],
		['memberLastName', inputMemberLastName || null],
		['memberFirstName', inputMemberFirstName || null],
		['memberId', inputMemberMemberId || null],
		['eyemedMemberId', inputMemberEmMemberId || null],
	];

	const arrayFilters: [string, string, string[]][] = [
		['clientCodes', 'clientCodes', clientCodes],
		['groupCodes', 'groupCodes', groupCodes],
		['planCodes', 'planCodes', planCodes],
		['relationshipTypes', 'relationshipTypes', inputMemberRelationshipTypes],
	];

	const dateRangeFilters: [
		string,
		string,
		typeof billingMonthStart,
		typeof billingMonthEnd,
		(date: dayjs.Dayjs | null) => string,
	][] = [
		[
			'subscriptionDateStart',
			'subscriptionDateEnd',
			inputMemberSubscriptionStartDate,
			inputMemberSubscriptionEndDate,
			formatDayjsForBE,
		],
		[
			'billingMonthStart',
			'billingMonthEnd',
			billingMonthStart,
			billingMonthEnd,
			formatDayjsMonthForBE,
		],
	];

	let queryParams = '';
	const body: Record<string, any> = {};

	for (const [key, value] of simpleFilters) {
		if (value === null) continue;
		queryParams += `&${key}=${value}`;
		body[key] = value;
	}

	for (const [queryKey, bodyKey, values] of arrayFilters) {
		if (!values || values.includes('All')) continue;
		queryParams += values.map(v => `&${queryKey}=${v}`).join('');
		body[bodyKey] = values;
	}

	for (const [
		startKey,
		endKey,
		startDate,
		endDate,
		formatter,
	] of dateRangeFilters) {
		if (!startDate && !endDate) continue;
		if (startDate) {
			const start = formatter(startDate);
			queryParams += `&${startKey}=${start}`;
			body[startKey] = start;
		}
		const effectiveEndDate = endDate || startDate;
		if (effectiveEndDate) {
			const end = formatter(effectiveEndDate);
			queryParams += `&${endKey}=${end}`;
			body[endKey] = end;
		}
	}

	return { queryParams, body };
};

export const numericValueToTimeString = (value: number | undefined): string => {
	if (typeof value === 'number') {
		const hours = Math.floor(value);
		const minutes = (value - hours) * 60;
		const formattedMinutes = Math.round(minutes);
		return `${hours}:${formattedMinutes.toString().padStart(2, '0')}`;
	} else return '';
};

/**
 * Paginate array
 * @param array List of element to be paginated
 * @param page_size PAGE_SIZE const
 * @param page_number Page number requested
 * @returns the slice of array corresponding to that page
 */

export const paginate = (
	array: any[], // TODO: fix any, use generic type array
	pageSize: number,
	pageNumber: number,
) => {
	return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
};

export const formatZuluForBE = (date: dayjs.Dayjs | null) => {
	return dayjs(date).tz('UTC').format('YYYY-MM-DDTHH:mm[Z]') || '';
};

export const getNumberFromString = (number: string) => {
	return formatNumber(number.replace(/[^\d.,]/g, ''), false);
};

export const generateColor = (color: string, type: 'light' | 'dark') => {
	//valore hex 30%
	const LIGHT_PERC = '4D';
	//valore hex 60%
	const DARK_PERC = '99';

	return `${color}${type === 'light' ? LIGHT_PERC : DARK_PERC}`;
};

// use the first value found either from the Lens or the Contacts materials
export const extractReviewRetailNetOfTax = (claimData: IClaim) => {
	return (
		claimData.amount.components.filter(
			component => component.serviceId === 1 || component.serviceId === 5,
		)[0]?.retailNetOfTax ?? 0
	);
};

export function formatMarkdown(markdown: string | undefined) {
	if (!markdown) {
		return '';
	}

	// Normalize specific terms using a single, case-insensitive regex
	let formattedText = markdown
		.replace(/\bcovid[\s-]*19\b/gi, 'COVID-19')
		.replace(/\bnaso[\s-]*faringeo\b/gi, 'NASO-FARINGEO')
		.replace(/S\.\s*S\.\s*N\./g, 'S.S.N.')
		.replace(/oppure\s*\n\s*-\s*/g, 'oppure\n* ');

	formattedText = replaceVowelWithAccent(formattedText);

	// Standardize list bullet points
	if (
		!formattedText.includes('COVID-19') &&
		!formattedText.includes('NASO-FARINGEO') &&
		!formattedText.includes('ozono') &&
		!formattedText.includes('OPT') &&
		!formattedText.includes('fisiokinesiterapia')
	) {
		formattedText = formattedText.replace(/(?:\n)?\s*[-*•]\s*/g, '\n* ');
	}

	// Convert multi-line paragraphs into single lines for better formatting.
	formattedText = formattedText.replaceAll('\n', ' \n\n');

	formattedText = formattedText
		.replace(/\b\w+\s,/g, match => match.replace(/,/g, '')) // replace [word ,] with [word, ]
		.replace(/(,|')([a-zA-Z])/g, '$1 $2'); // adds a space after a comma or apostrophe if it is followed by a letter

	// Handle special case for faschim website
	if (!formattedText.includes('www.faschim.it')) {
		formattedText = formattedText.replace(/(\s+\.\s*)([a-zA-Z0-9])/g, '. $2'); // adds a space after a period if it is followed by a letter or number
	}

	// Handle the special case of a colon followed by a list.
	// This looks for a colon followed by a newline and a list item, then adds a new line before it.
	formattedText = formattedText.replace(/(\s*:)\n(\*)/g, '$1\n\n$2');

	// Remove double special characters
	formattedText = formattedText.replace(/""|\.{2,}/g, match =>
		match.startsWith('"') ? '' : '.',
	);

	// Remove multiple spaces
	formattedText = formattedText.replace(/\s{5,}/g, ' ');

	// handle special case \\n
	formattedText = formattedText.replace(/\\n/g, '\n');

	return formattedText.trim();
}

function replaceVowelWithAccent(str: string) {
	let formattedSpace = str.replace(/'(?! )/g, "' ");

	let result = formattedSpace.replace(
		/[aA]'|[eE]'|[iI]'|[oO]'|[uU]'/g,
		match => {
			switch (match) {
				case "a'":
					return 'à';
				case "A'":
					return 'À';
				case "e'":
					return 'è';
				case "E'":
					return 'È';
				case "i'":
					return 'ì';
				case "I'":
					return 'Ì';
				case "o'":
					return 'ò';
				case "O'":
					return 'Ò';
				case "u'":
					return 'ù';
				case "U'":
					return 'Ù';
				default:
					return match;
			}
		},
	);

	return result;
}

export const buildFraudFilterParams = (form: IFilterState) => {
	const {
		inputFraudId,
		inputClaimId,
		inputDetectionDate,
		inputReferralDate,
		inputInvestigationStatus,
	} = form;

	const filters: [string, string | null][] = [
		['fraudId', inputFraudId || null],
		['claimId', inputClaimId || null],
		[
			'detectionDate',
			inputDetectionDate ? formatDayjsForBE(inputDetectionDate) : null,
		],
		[
			'referralDate',
			inputReferralDate ? formatDayjsForBE(inputReferralDate) : null,
		],
		['investigationStatus', inputInvestigationStatus || null],
	];

	let queryParams = '';
	const body: Record<string, string | null> = {};
	for (const [key, value] of filters) {
		if (value === null) continue;
		queryParams += `&${key}=${value}`;
		body[key] = value;
	}
	return { queryParams, body };
};

export const buildComplaintFilterParams = (form: IFilterState) => {
	const {
		inputComplaintId,
		inputClaimId,
		inputComplaintSource,
		inputComplaintType,
		inputComplaintNotificationDate,
		inputComplaintStatus,
	} = form;

	const filters: [string, string | null][] = [
		['complaintId', inputComplaintId || null],
		['claimId', inputClaimId || null],
		['complaintSource', inputComplaintSource || null],
		['complaintType', inputComplaintType || null],
		[
			'complaintNotificationDate',
			inputComplaintNotificationDate
				? formatDayjsForBE(inputComplaintNotificationDate)
				: null,
		],
		['complaintStatus', inputComplaintStatus || null],
	];

	let queryParams = '';
	const body: Record<string, string | null> = {};
	for (const [key, value] of filters) {
		if (value === null) continue;
		queryParams += `&${key}=${value}`;
		body[key] = value;
	}
	return { queryParams, body };
};

export const bytesToKilobytes = (bytes: number) => {
	return Math.round(bytes / 1024) > 1024
		? [formatNumber(String(bytes / 1024 / 1024), false), 'MB']
		: [Math.round(bytes / 1024), 'kB'];
};